import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { translations, SupportedLanguage } from "@/locales";

type I18nContextValue = {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
};

const STORAGE_KEY = "app.language";
const DEFAULT_LANGUAGE: SupportedLanguage = "en";

const I18nContext = createContext<I18nContextValue>({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => undefined,
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    if (typeof window === "undefined") {
      return DEFAULT_LANGUAGE;
    }
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && stored in translations) {
      return stored as SupportedLanguage;
    }
    return DEFAULT_LANGUAGE;
  });

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, lang);
    }
  }, []);

  const value = useMemo(() => ({ language, setLanguage }), [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useTranslation(namespace: keyof (typeof translations)["en"]) {
  const { language } = useI18n();
  return useCallback(
    (key: string, fallback?: string) => {
      const locale = translations[language] ?? translations[DEFAULT_LANGUAGE];
      const ns = locale[namespace] ?? {};
      const value = key.split(".").reduce<any>((acc, segment) => {
        if (acc && typeof acc === "object" && segment in acc) {
          return acc[segment];
        }
        return undefined;
      }, ns);
      if (typeof value === "string") {
        return value;
      }
      return fallback ?? key;
    },
    [language, namespace],
  );
}
